import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "./utils";
import { Spinner } from "./Spinner";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-Border_radius-medium font-medium transition-colors duration-200 focus-visible:outline-none disabled:pointer-events-none gap-2",
  {
    variants: {
      variant: {
        primary: [
          "bg-Color-Background-brand-default text-Color-Text-neutral-white",
          "hover:bg-Color-Background-brand-hover",
          "active:bg-Color-Background-brand-pressed",
          "focus-visible:ring-4 focus-visible:ring-inset focus-visible:ring-Color-Stroke-focus",
          "disabled:bg-Color-Background-brand-disabled disabled:text-Color-Text-neutral-white disabled:cursor-not-allowed",
        ],
        secondary: [
          "bg-Color-Background-brand-inverted text-Color-Text-brand-default border border-Color-Stroke-brand",
          "hover:bg-Color-Background-brand-disabled hover:text-Color-Text-brand-default hover:border-Color-Stroke-brand",
          "active:bg-Color-Background-brand-disabled active:text-Color-Text-brand-default active:border-Color-Stroke-brand",
          "focus-visible:ring-4 focus-visible:ring-inset focus-visible:ring-Color-Stroke-focus",
          "disabled:bg-Color-Background-brand-inverted disabled:text-Color-Text-brand-disabled disabled:border-Color-Stroke-brand disabled:cursor-not-allowed",
        ],
        tertiary: [
          "bg-Color-Background-neutral-default_secondary text-Color-Text-neutral-black border border-Color-Stroke-neutral",
          "hover:bg-Color-Background-neutral-hover hover:text-Color-Text-neutral-black hover:border-Color-Stroke-neutral",
          "active:bg-Color-Background-neutral-pressed active:text-Color-Text-neutral-black active:border-Color-Stroke-neutral",
          "focus-visible:ring-4 focus-visible:ring-inset focus-visible:ring-Color-Stroke-focus",
          "disabled:bg-Color-Background-neutral-default_secondary disabled:text-Color-Text-neutral-disabled disabled:border-Color-Stroke-neutral disabled:cursor-not-allowed",
        ],
        destructive: [
          "bg-Color-Background-negative-default text-Color-Text-neutral-white",
          "hover:bg-Color-Background-negative-hover",
          "active:bg-Color-Background-negative-pressed",
          "focus-visible:ring-4 focus-visible:ring-inset focus-visible:ring-Color-Stroke-negative",
          "disabled:bg-Color-Background-negative-disabled disabled:text-Color-Text-neutral-white disabled:cursor-not-allowed",
        ],
        ghost: [
          "bg-transparent text-Color-Text-neutral-black hover:bg-Color-Background-neutral-hover",
          "hover:text-Color-Text-neutral-black",
          "active:bg-Color-Background-neutral-pressed active:text-Color-Text-neutral-black",
          "focus-visible:ring-4 focus-visible:ring-inset focus-visible:ring-Color-Stroke-focus",
          "disabled:bg-transparent disabled:text-Color-Text-neutral-disabled disabled:cursor-not-allowed",
        ],
      },
      size: {
        sm: "h-8 px-3 text-sm font-semibold min-w-[2rem]",
        md: "h-10 px-4 text-sm font-semibold min-w-[2.5rem]",
        lg: "h-12 px-6 text-base font-semibold min-w-[3rem]",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  },
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** Render as child element using Radix Slot */
  asChild?: boolean;
  /** Show loading spinner and disable button */
  loading?: boolean;
  /** Icon to show on the left side */
  leftIcon?: React.ReactNode;
  /** Icon to show on the right side */
  rightIcon?: React.ReactNode;
}

/**
 * Button component following MEBI Design System
 *
 * @example
 * ```tsx
 * <Button variant="primary" size="md">
 *   Primary Button
 * </Button>
 *
 * <Button variant="secondary" size="lg" loading>
 *   Loading Button
 * </Button>
 *
 * <Button variant="tertiary" leftIcon={<PlusIcon />}>
 *   With Icon
 * </Button>
 * ```
 */

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loading = false,
      leftIcon,
      rightIcon,
      children,
      disabled,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    const isDisabled = loading || disabled;

    // Get spinner color based on variant and state
    const getSpinnerVariant = () => {
      if (loading) {
        switch (variant) {
          case "primary":
          case "destructive":
            return "white";
          case "secondary":
            return "primary";
          case "tertiary":
          case "ghost":
            return "neutral";
          default:
            return "white";
        }
      }
      return "white";
    };

    // Get spinner size based on button size
    const getSpinnerSize = () => {
      switch (size) {
        case "sm":
          return "sm";
        case "lg":
          return "lg";
        default:
          return "md";
      }
    };

    return (
      <Comp
        className={cn(buttonVariants({ variant, size }), className)}
        ref={ref}
        disabled={isDisabled}
        {...props}
      >
        {/* Loading state: show spinner, hide text and icons */}
        {loading ? (
          <Spinner size={getSpinnerSize()} variant={getSpinnerVariant()} />
        ) : (
          <>
            {/* Left icon - optional */}
            {leftIcon && (
              <span className="flex-shrink-0 flex items-center justify-center w-5 h-5">
                {leftIcon}
              </span>
            )}
            {/* Button text */}
            {children && <span className="truncate">{children}</span>}
            {/* Right icon - optional */}
            {rightIcon && (
              <span className="flex-shrink-0 flex items-center justify-center w-5 h-5">
                {rightIcon}
              </span>
            )}
          </>
        )}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export default Button;
export { Button };
